'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import AshaIcon from '@/components/ui/AshaIcon';

const slides = [
{
  url: "https://img.rocket.new/generatedImages/rocket_gen_img_16b8c5b0b-1767916386714.png",
  alt: 'Physical therapist assisting patient with leg rehabilitation exercises on treatment table'
},
{
  url: "https://img.rocket.new/generatedImages/rocket_gen_img_1bc42968b-1772143920272.png",
  alt: 'Rehabilitation specialist guiding elderly patient through walking recovery exercises'
},
{
  url: "https://img.rocket.new/generatedImages/rocket_gen_img_1dd05c8c7-1768008355299.png",
  alt: 'Sports injury therapist performing shoulder assessment on athlete in modern clinic'
},
{
  url: "https://img.rocket.new/generatedImages/rocket_gen_img_11957dfc5-1765095400107.png",
  alt: 'Physical therapy session with patient performing balance and strength recovery exercises'
}];


const trustBadges = [
'JCAHO Accredited',
'18 Clinic Locations',
'Most Major Insurance Accepted'];


export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [prevSlide, setPrevSlide] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setTransitioning(true);
      setPrevSlide(currentSlide);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setTransitioning(false);
        setPrevSlide(null);
      }, 800);
    }, 5000);
    return () => clearInterval(interval);
  }, [currentSlide]);

  const goToSlide = (idx: number) => {
    if (idx === currentSlide) return;
    setTransitioning(true);
    setPrevSlide(currentSlide);
    setTimeout(() => {
      setCurrentSlide(idx);
      setTransitioning(false);
      setPrevSlide(null);
    }, 800);
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
      {/* Slideshow Background */}
      <div className="absolute inset-0 z-0">
        {slides.map((slide, idx) =>
        <div
          key={slide.url}
          className="absolute inset-0 transition-opacity duration-800"
          style={{
            opacity: idx === currentSlide ? 1 : idx === prevSlide && transitioning ? 0 : 0,
            transition: 'opacity 0.8s ease-in-out'
          }}>

            <Image
            src={slide.url}
            alt={slide.alt}
            fill
            className="object-cover object-center"
            priority={idx === 0}
            sizes="100vw" />

          </div>
        )}
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2A43]/90 via-[#0A2A43]/70 to-[#0A2A43]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2A43]/60 via-transparent to-transparent" />
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, idx) =>
        <button
          key={`dot-${idx}`}
          onClick={() => goToSlide(idx)}
          aria-label={`Go to slide ${idx + 1}`}
          className="transition-all duration-300 rounded-full"
          style={{
            width: idx === currentSlide ? '28px' : '8px',
            height: '8px',
            background: idx === currentSlide ? 'var(--secondary)' : 'rgba(255,255,255,0.45)'
          }} />

        )}
      </div>

      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 w-full py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border"
            style={{ background: 'rgba(27,175,191,0.15)', borderColor: 'rgba(27,175,191,0.4)', color: '#1BAFBF' }}>
              <span className="w-2 h-2 rounded-full bg-secondary animate-pulse-slow" />
              Accepting New Patients — May 2026
            </div>

            {/* Headline */}
            <div>
              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-tight tracking-tight">
                Restoring{' '}
                <span className="text-secondary">Mobility.</span>
                <br />
                Rebuilding{' '}
                <span className="relative">
                  Lives.
                  <span className="absolute -bottom-1 left-0 right-0 h-1 rounded-full bg-secondary opacity-40" />
                </span>
              </h1>
              <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-xl">
                ASHA Recovery Group manages a network of specialized physical therapy and rehabilitation clinics — delivering evidence-based care that gets patients back to what matters most.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#locations"
                className="btn-secondary flex items-center gap-2 text-base px-6 py-3.5 rounded-2xl">
                <AshaIcon name="find-clinic" size={18} color="white" />
                Find a Clinic
              </a>
              <a
                href="#contact"
                className="flex items-center gap-2 text-base px-6 py-3.5 rounded-2xl border border-white/30 text-white hover:bg-white/10 transition-colors font-semibold">
                <AshaIcon name="patient-portal" size={18} color="white" />
                Request Appointment
                <span className="ml-1 opacity-60 text-base">→</span>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 pt-2">
              {trustBadges?.map((badge) =>
              <div key={`badge-${badge}`} className="flex items-center gap-2">
                  <AshaIcon name="success-recovery" size={16} className="flex-shrink-0" color="#1BAFBF" />
                  <span className="text-sm text-white/80 font-medium">{badge}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right — Cards Column */}
          <div className="relative flex flex-col gap-5 items-center lg:items-end">
            {/* Grethel Lopez Profile Card */}
            <div className="w-full max-w-sm bg-white/10 backdrop-blur-md rounded-3xl p-5 border border-white/20 shadow-xl flex items-center gap-4">
              <div className="relative w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-secondary/60">
                <Image
                  src="/assets/images/WhatsApp_Image_2026-03-11_at_9.52.29_PM-1777630669856.jpeg"
                  alt="Grethel Lopez, CEO and Practice Lead Technician of ASHA Recovery Group"
                  fill
                  className="object-cover object-top"
                  sizes="80px" />

              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-base leading-tight">Grethel Lopez</p>
                <p className="text-secondary text-xs font-semibold mt-0.5">CEO & Practice Lead Technician</p>
                <p className="text-white/60 text-xs mt-1.5 leading-snug">
                  "Our mission is to restore function and rebuild confidence — one patient at a time."
                </p>
              </div>
            </div>

            {/* Rehab Stats Card */}
            <div className="w-full max-w-sm rounded-3xl p-6 shadow-2xl"
            style={{ background: 'var(--primary)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(27,175,191,0.2)' }}>
                    <AshaIcon name="post-surgical-rehab" size={22} color="#1BAFBF" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs font-medium uppercase tracking-widest">Current Program</p>
                    <p className="text-white font-bold text-base">Post-Surgical Rehab</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                  { label: 'Range of Motion', value: 78 },
                  { label: 'Strength Recovery', value: 62 },
                  { label: 'Pain Management', value: 91 }]?.
                  map((item) =>
                  <div key={`hero-metric-${item?.label}`}>
                      <div className="flex justify-between mb-1">
                        <span className="text-white/70 text-xs font-medium">{item?.label}</span>
                        <span className="text-white text-xs font-bold tabular-nums">{item?.value}%</span>
                      </div>
                      <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }}>
                        <div
                        className="h-full rounded-full"
                        style={{ width: `${item?.value}%`, background: 'var(--secondary)' }} />

                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                  <div>
                    <p className="text-white/60 text-xs">Next Session</p>
                    <p className="text-white font-semibold text-sm">Mon, May 4 · 10:00 AM</p>
                  </div>
                  <div className="px-3 py-1.5 rounded-xl text-xs font-bold"
                  style={{ background: 'rgba(27,175,191,0.2)', color: 'var(--secondary)' }}>
                    On Track
                  </div>
                </div>
              </div>
            </div>

            {/* Floating stat chips */}
            <div className="flex gap-3 w-full max-w-sm">
              <div className="flex-1 bg-white/10 backdrop-blur-md rounded-2xl px-4 py-3 border border-white/20">
                <p className="text-white/60 text-xs font-medium uppercase tracking-wide">Active Patients</p>
                <p className="text-2xl font-extrabold text-white tabular-nums">2,847</p>
                <p className="text-xs text-secondary font-semibold">↑ 12% this month</p>
              </div>
              <div className="flex-1 bg-white/10 backdrop-blur-md rounded-2xl px-4 py-3 border border-white/20">
                <p className="text-white/60 text-xs font-medium uppercase tracking-wide">Recovery Rate</p>
                <p className="text-2xl font-extrabold text-white tabular-nums">94.2%</p>
                <p className="text-xs text-secondary font-semibold">Above national avg</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}